"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.slateToMarkdown = void 0;
const types_1 = require("../types");
const guards_1 = require("../types/guards");
const textToMarkdown = (node) => {
    let text = node.text;
    text = text.replace(/([\\`*_{}[\]()#+\-.!|])/g, '\\$1');
    if (node[types_1.SlateMark.bold] && node[types_1.SlateMark.italic]) {
        text = `***${text}***`;
    }
    else if (node[types_1.SlateMark.bold]) {
        text = `**${text}**`;
    }
    else if (node[types_1.SlateMark.italic]) {
        text = `*${text}*`;
    }
    if (node[types_1.SlateMark.strikethrough]) {
        text = `~~${text}~~`;
    }
    if (node[types_1.SlateMark.underline]) {
        text = `<u>${text}</u>`;
    }
    if (node[types_1.SlateMark.register] === 'sub') {
        text = `<sub>${text}</sub>`;
    }
    else if (node[types_1.SlateMark.register] === 'sup') {
        text = `<sup>${text}</sup>`;
    }
    const styles = [];
    if (node[types_1.SlateMark.color]) {
        styles.push(`color: ${node[types_1.SlateMark.color]}`);
    }
    if (node[types_1.SlateMark.backgroundColor]) {
        styles.push(`background-color: ${node[types_1.SlateMark.backgroundColor]}`);
    }
    if (node[types_1.SlateMark.textTransform]) {
        styles.push(`text-transform: ${node[types_1.SlateMark.textTransform]}`);
    }
    if (styles.length > 0) {
        text = `<span style="${styles.join('; ')}">${text}</span>`;
    }
    return text;
};
const childrenToMarkdown = (children) => {
    return children
        .map((child) => {
        if ((0, guards_1.isTextNode)(child)) {
            return textToMarkdown(child);
        }
        else if ((0, guards_1.isInlineElement)(child)) {
            return inlineElementToMarkdown(child);
        }
        else if ((0, guards_1.isBlockElement)(child) || (0, guards_1.isListElement)(child)) {
            return nodeToMarkdown(child);
        }
        return '';
    })
        .join('');
};
const inlineElementToMarkdown = (node) => {
    const childrenContent = childrenToMarkdown(node.children);
    switch (node.type) {
        case types_1.SlateElementType.link: {
            const linkNode = node;
            return `[${childrenContent}](${linkNode.url})`;
        }
        case types_1.SlateElementType.crossLink: {
            const crossLinkNode = node;
            const href = `~/${crossLinkNode.value.section}/${crossLinkNode.value.block || ''}`;
            return `[${childrenContent}](${href})`;
        }
        case types_1.SlateElementType.mention: {
            const mentionNode = node;
            return `[@${mentionNode.name || mentionNode.employeeId}](mention:${mentionNode.employeeId})`;
        }
        case types_1.SlateElementType.annotation: {
            const annotationNode = node;
            return `<span title="${annotationNode.value}">${childrenContent}</span>`;
        }
        case types_1.SlateElementType.code:
            return `\`${childrenContent}\``;
        default:
            return childrenContent;
    }
};
const nodeToMarkdown = (node, listDepth = 0) => {
    if ((0, guards_1.isTextNode)(node)) {
        return textToMarkdown(node);
    }
    if ((0, guards_1.isInlineElement)(node)) {
        return inlineElementToMarkdown(node);
    }
    const elementNode = node;
    const childrenContent = childrenToMarkdown(elementNode.children);
    const hasAlignment = 'textAlign' in elementNode && elementNode.textAlign;
    const wrapWithAlignment = (content) => {
        if (hasAlignment && 'textAlign' in elementNode) {
            return `<div style="text-align: ${elementNode.textAlign}">${content}</div>`;
        }
        return content;
    };
    switch (elementNode.type) {
        case types_1.SlateElementType.heading1:
            return wrapWithAlignment(`# ${childrenContent}\n\n`);
        case types_1.SlateElementType.heading2:
            return wrapWithAlignment(`## ${childrenContent}\n\n`);
        case types_1.SlateElementType.heading3:
            return wrapWithAlignment(`### ${childrenContent}\n\n`);
        case types_1.SlateElementType.heading4:
            return wrapWithAlignment(`#### ${childrenContent}\n\n`);
        case types_1.SlateElementType.blockquote:
            return `> ${childrenContent.replace(/\n/g, '\n> ')}\n\n`;
        case types_1.SlateElementType.bulletedList: {
            const listItems = elementNode.children
                .map((item) => {
                if ((0, guards_1.isBlockElement)(item) && item.type === types_1.SlateElementType.listItem) {
                    const indent = '  '.repeat(listDepth);
                    const itemContent = childrenToMarkdown(item.children);
                    return `${indent}- ${itemContent}`;
                }
                return '';
            })
                .filter(Boolean)
                .join('\n');
            return `${listItems}\n\n`;
        }
        case types_1.SlateElementType.numberedList: {
            const listItems = elementNode.children
                .map((item, index) => {
                if ((0, guards_1.isBlockElement)(item) && item.type === types_1.SlateElementType.listItem) {
                    const indent = '  '.repeat(listDepth);
                    const itemContent = childrenToMarkdown(item.children);
                    return `${indent}${index + 1}. ${itemContent}`;
                }
                return '';
            })
                .filter(Boolean)
                .join('\n');
            return `${listItems}\n\n`;
        }
        case types_1.SlateElementType.listItem:
            return childrenContent;
        case types_1.SlateElementType.caption:
            return `<div role="caption">${childrenContent}</div>\n\n`;
        case types_1.SlateElementType.elementDefault:
        default:
            return wrapWithAlignment(`${childrenContent}\n\n`);
    }
};
const slateToMarkdown = (nodes, options = {}) => {
    if (!nodes || nodes.length === 0) {
        return '';
    }
    const allowHtmlStyles = options.allowHtmlStyles !== false;
    const allowAlignment = options.allowAlignment !== false;
    const allowCaptionHtml = options.allowCaptionHtml !== false;
    const textToMarkdownOpt = (node) => {
        let text = node.text;
        text = text.replace(/([\\`*_{}[\]()#+\-.!|])/g, '\\$1');
        if (node[types_1.SlateMark.bold] && node[types_1.SlateMark.italic]) {
            text = `***${text}***`;
        }
        else if (node[types_1.SlateMark.bold]) {
            text = `**${text}**`;
        }
        else if (node[types_1.SlateMark.italic]) {
            text = `*${text}*`;
        }
        if (node[types_1.SlateMark.strikethrough]) {
            text = `~~${text}~~`;
        }
        if (allowHtmlStyles) {
            if (node[types_1.SlateMark.underline]) {
                text = `<u>${text}</u>`;
            }
            if (node[types_1.SlateMark.register] === 'sub') {
                text = `<sub>${text}</sub>`;
            }
            else if (node[types_1.SlateMark.register] === 'sup') {
                text = `<sup>${text}</sup>`;
            }
            const styles = [];
            if (node[types_1.SlateMark.color]) {
                styles.push(`color: ${node[types_1.SlateMark.color]}`);
            }
            if (node[types_1.SlateMark.backgroundColor]) {
                styles.push(`background-color: ${node[types_1.SlateMark.backgroundColor]}`);
            }
            if (node[types_1.SlateMark.textTransform]) {
                styles.push(`text-transform: ${node[types_1.SlateMark.textTransform]}`);
            }
            if (styles.length > 0) {
                text = `<span style="${styles.join('; ')}">${text}</span>`;
            }
        }
        return text;
    };
    const childrenToMarkdownOpt = (children) => {
        return children
            .map((child) => {
            if ((0, guards_1.isTextNode)(child)) {
                return textToMarkdownOpt(child);
            }
            else if ((0, guards_1.isInlineElement)(child)) {
                return inlineElementToMarkdownOpt(child);
            }
            else if ((0, guards_1.isBlockElement)(child) || (0, guards_1.isListElement)(child)) {
                return nodeToMarkdownOpt(child);
            }
            return '';
        })
            .join('');
    };
    const inlineElementToMarkdownOpt = (node) => {
        const childrenContent = childrenToMarkdownOpt(node.children);
        switch (node.type) {
            case types_1.SlateElementType.link: {
                const linkNode = node;
                return `[${childrenContent}](${linkNode.url})`;
            }
            case types_1.SlateElementType.crossLink: {
                const crossLinkNode = node;
                const href = `~/${crossLinkNode.value.section}/${crossLinkNode.value.block || ''}`;
                return `[${childrenContent}](${href})`;
            }
            case types_1.SlateElementType.mention: {
                const mentionNode = node;
                return `[@${mentionNode.name || mentionNode.employeeId}](mention:${mentionNode.employeeId})`;
            }
            case types_1.SlateElementType.annotation: {
                const annotationNode = node;
                return allowHtmlStyles
                    ? `<span title="${annotationNode.value}">${childrenContent}</span>`
                    : childrenContent;
            }
            case types_1.SlateElementType.code:
                return `\`${childrenContent}\``;
            default:
                return childrenContent;
        }
    };
    const nodeToMarkdownOpt = (node, listDepth = 0) => {
        if ((0, guards_1.isTextNode)(node)) {
            return textToMarkdownOpt(node);
        }
        if ((0, guards_1.isInlineElement)(node)) {
            return inlineElementToMarkdownOpt(node);
        }
        const elementNode = node;
        const childrenContent = childrenToMarkdownOpt(elementNode.children);
        const wrapWithAlignment = (content) => {
            const hasAlignment = 'textAlign' in elementNode && elementNode.textAlign;
            if (allowAlignment && hasAlignment) {
                return `<div style="text-align: ${elementNode.textAlign}">${content}</div>`;
            }
            return content;
        };
        switch (elementNode.type) {
            case types_1.SlateElementType.heading1:
                return wrapWithAlignment(`# ${childrenContent}\n\n`);
            case types_1.SlateElementType.heading2:
                return wrapWithAlignment(`## ${childrenContent}\n\n`);
            case types_1.SlateElementType.heading3:
                return wrapWithAlignment(`### ${childrenContent}\n\n`);
            case types_1.SlateElementType.heading4:
                return wrapWithAlignment(`#### ${childrenContent}\n\n`);
            case types_1.SlateElementType.blockquote:
                return `> ${childrenContent.replace(/\n/g, '\n> ')}\n\n`;
            case types_1.SlateElementType.bulletedList: {
                const listItems = elementNode.children
                    .map((item) => {
                    if ((0, guards_1.isBlockElement)(item) && item.type === types_1.SlateElementType.listItem) {
                        const indent = '  '.repeat(listDepth);
                        const itemContent = childrenToMarkdownOpt(item.children);
                        return `${indent}- ${itemContent}`;
                    }
                    return '';
                })
                    .filter(Boolean)
                    .join('\n');
                return `${listItems}\n\n`;
            }
            case types_1.SlateElementType.numberedList: {
                const listItems = elementNode.children
                    .map((item, index) => {
                    if ((0, guards_1.isBlockElement)(item) && item.type === types_1.SlateElementType.listItem) {
                        const indent = '  '.repeat(listDepth);
                        const itemContent = childrenToMarkdownOpt(item.children);
                        return `${indent}${index + 1}. ${itemContent}`;
                    }
                    return '';
                })
                    .filter(Boolean)
                    .join('\n');
                return `${listItems}\n\n`;
            }
            case types_1.SlateElementType.listItem:
                return childrenContent;
            case types_1.SlateElementType.caption:
                return allowCaptionHtml
                    ? `<div role="caption">${childrenContent}</div>\n\n`
                    : `${childrenContent}\n\n`;
            case types_1.SlateElementType.elementDefault:
            default:
                return wrapWithAlignment(`${childrenContent}\n\n`);
        }
    };
    const markdown = nodes
        .map((node) => nodeToMarkdownOpt(node))
        .join('')
        .trim();
    return markdown;
};
exports.slateToMarkdown = slateToMarkdown;
